/*
 *** Usage in FO: OK
 *** Usage in PO: OK
 *** Usage in BO: NOT OK
 */

import PropTypes from 'prop-types';
import { useMemo } from 'react';
import Dotdotdot from 'react-dotdotdot';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Image } from '@hivebrite/design-system/Image';
import { breakText, focusOutline } from '@hivebrite/design-system/Theme';

import { Link } from 'hb-react/frontoffice/components/Link';
import HtmlContent from 'hb-react/shared/components/HtmlContent';
import IconStarCircle from 'hb-react/shared/icons/StarCircle';
import { iOS } from 'helpers/mobileDeviceFromUserAgent';

const UserPresentation = ({
  customSubHeader,
  dataTestId,
  header,
  imageAlt,
  imageLink,
  imageLinkIsExternal,
  imageUrl,
  isUserBookmarked,
  imageSize,
  subHeader,
  topRightItem,
}) => {
  const displayPicture = useMemo(() => {
    const img = (
      <>
        <Image src={imageUrl} alt={imageAlt || ''} size={imageSize} />
        {isUserBookmarked && (
          <Box
            position="absolute"
            right="-2px"
            bottom="-2px"
            size={20}
            color="primary"
          >
            <IconStarCircle height="100%" width="100%" />
          </Box>
        )}
      </>
    );

    if (imageLink) {
      return imageLinkIsExternal ? (
        <Link to={imageLink} target="_blank" aria-hidden="true" tabIndex={-1}>
          {img}
        </Link>
      ) : (
        <Link to={imageLink} aria-hidden="true" tabIndex={-1}>
          {img}
        </Link>
      );
    }

    return img;
  }, [header, imageUrl, imageLink, imageLinkIsExternal, isUserBookmarked]);

  return (
    <UserPresentationWrapper data-testid={dataTestId}>
      <AvatarWrapper imageSize={imageSize}>{displayPicture}</AvatarWrapper>
      <Box
        flex={1}
        flexDirection="row"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Box
          height="100%"
          flexDirection="column"
          justifyContent="center"
          pb="2px"
          flex={1}
        >
          <Dotdotdot clamp={2} useNativeClamp={!iOS()}>
            <Box font="h4-semibold" px={1} pt="2px" display="block">
              {header}
            </Box>
          </Dotdotdot>
          {subHeader && (
            <Box mt={1}>
              <Dotdotdot clamp={1} useNativeClamp>
                <Box display="block" font="metadata-regular-asphalt" pl={1}>
                  <HtmlContent content={s__(subHeader)} />
                </Box>
              </Dotdotdot>
            </Box>
          )}
          {customSubHeader && (
            <Box mt={1} px={1}>
              {customSubHeader}
            </Box>
          )}
        </Box>
        {topRightItem}
      </Box>
    </UserPresentationWrapper>
  );
};

export default UserPresentation;

UserPresentation.propTypes = {
  header: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
  imageUrl: PropTypes.string.isRequired,
  customSubHeader: PropTypes.node,
  imageAlt: PropTypes.string,
  imageLink: PropTypes.string,
  imageLinkIsExternal: PropTypes.bool,
  imageSize: PropTypes.number,
  isUserBookmarked: PropTypes.bool,
  subHeader: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  dataTestId: PropTypes.string,
  topRightItem: PropTypes.node,
};

UserPresentation.defaultProps = {
  imageLinkIsExternal: false,
  isUserBookmarked: false,
  imageSize: 60,
};

const UserPresentationWrapper = styled(Box)`
  overflow-wrap: anywhere;
`;

const AvatarWrapper = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) => !['imageSize'].includes(prop),
  })
  .attrs(({ imageSize }) => ({
    height: imageSize,
    mr: 3,
    width: imageSize,
    position: 'relative',
  }))`
  border-radius: 50%;
  user-select: none;
  ${breakText}

  a {
    ${focusOutline}
    border-radius: 50%;
  }

  img {
    border-radius: 50%;
    width: inherit;
  }
`;
